<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Customers from '@/services/Customers';
import Ticketing from '@/services/Ticketing';
import General from '@/services/General';
import VueBootstrapTypeahead from 'vue-bootstrap-typeahead';
import modalViewMatchTicket from "@/components/modals/ticketing/modalViewMatchTicket";
import modalViewSeasonTicket from "@/components/modals/ticketing/modalViewSeasonTicket";
import _ from 'underscore';

export default {
  components: { Layout, PageHeader, VueBootstrapTypeahead, modalViewMatchTicket, modalViewSeasonTicket },
  page: {
    title: "Ticketing User Data",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  watch: {
    'filterInput.email': _.debounce(function(search) { this.getCustomersAutocomplete(search, 'email') }, 900),
    'filterInput.provider_ticketing_id': _.debounce(function(search) { this.getCustomersAutocomplete(search, 'provider_ticketing_id') }, 900),
  },
  data() {
    return {
      title: "Ticketing User Data",
      items: [
        {
          text: "Ticketing",
          href: "/ticketing",
        },
        {
          text: "Ticketing User Data",
          active: true,
        },
      ],
      filterInput: {
        email: '',
        provider_ticketing_id: '',
      },
      emailSuggestions: [],
      ticketingIdSuggestions: [],
      tableData: [],
      ticketingData: null,
      isBusy: false,
      ticketsBusy: false,
      hasSearched: false,
      error: null,
      seasonTicketsShowAll: [],
      matchTicketsDefault: [],
      matchTicketsByCustomerNumber: [],
      seasonTicketsByCustomerNumber: [],
      modalData: [],
      perPage: 5,
      ssoSeasonPage: 1,
      ssoMatchPage: 1,
      cnSeasonPage: 1,
      cnMatchPage: 1,
      seasonFields: [
        { key: "display_name", label: "Season" },
        { key: "subscription_number", label: "Subscription Number" },
        { key: "area_name", label: "Section" },
        { key: "row_name", label: "Row" },
        { key: "seat_name", label: "Seat" },
        { key: "price_type_name", label: "Price Type" },
        { key: "client_name", label: "Client Name" },
        { key: "price", label: "Price", tdClass: 'text-end' },
        { key: "action", label: "" },
      ],
      matchFields: [
        { key: "match.date_formatted", label: "Date" },
        { key: "match.match_id", label: "Match" },
        { key: "ticket_section", label: "Section" },
        { key: "ticket_row", label: "Row" },
        { key: "ticket_seat", label: "Seat" },
        { key: "client_name", label: "Client Name" },
        { key: "ticket_price", label: "Price", tdClass: 'text-end' },
        { key: "season_ticket", label: "Season Ticket", tdClass: 'text-center' },
        { key: "action", label: "" },
      ],
    };
  },
  computed: {
    customerRow() {
      return Array.isArray(this.tableData) && this.tableData.length
        ? this.tableData[0]
        : null;
    },
    ticketingRow() {
      return this.ticketingData && this.ticketingData.data
        ? this.ticketingData.data
        : null;
    },
    ticketingOriginal() {
      return this.ticketingRow && this.ticketingRow.providerOriginalData
        ? this.ticketingRow.providerOriginalData
        : null;
    },

    // Normalized, comparable values
    crmFullName() {
      if (!this.customerRow) return '';
      const parts = [
        this.customerRow.first_name,
        this.customerRow.middle_name,
        this.customerRow.last_name,
      ].filter(Boolean);
      return parts.join(' ');
    },
    ticketingFullName() {
      if (!this.ticketingOriginal) return '';
      const parts = [
        this.ticketingOriginal.FirstName,
        this.ticketingOriginal.MiddleName,
        this.ticketingOriginal.LastName,
      ].filter(Boolean);
      return parts.join(' ');
    },
    crmAddress() {
      if (!this.customerRow) return '';
      const parts = [
        this.customerRow.address_line1,
        this.customerRow.address_line2,
        this.customerRow.address_line3,
        this.customerRow.address_city,
        this.customerRow.address_postal,
        this.customerRow.address_country,
      ].filter(Boolean);
      return parts.join(' ');
    },
    ticketingAddress() {
      if (!this.ticketingOriginal) return '';
      const parts = [
        this.ticketingOriginal.Street,
        this.ticketingOriginal.StreetNr,
        this.ticketingOriginal.City,
        this.ticketingOriginal.PostalCode,
        this.ticketingRow.countryIso2,
      ].filter(Boolean);
      return parts.join(' ');
    },
    // Difference flags
    fullNameDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        // If we don't have both sides, don't mark as different
        return false;
      }
      return this.areDifferent(this.crmFullName, this.ticketingFullName);
    },
    emailDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.email : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.Email : '';
      return this.areDifferent(crm, tkt);
    },
    dobDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.date_of_birth : '';
      let tkt = this.ticketingOriginal ? this.ticketingOriginal.DateOfBirth : '';
      if (tkt && typeof tkt === 'string' && tkt.includes('T')) {
        tkt = tkt.split('T')[0];
      }
      return this.areDifferent(crm, tkt);
    },
    genderDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.gender : '';
      const tkt = this.ticketingOriginal ? (this.ticketingOriginal.gender || '') : '';
      return this.areDifferent(crm, tkt);
    },
    phoneDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.phone_number : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.TelephoneNr : '';
      return this.areDifferent(crm, tkt);
    },
    addressDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      return this.areDifferent(this.crmAddress, this.ticketingAddress);
    },
    ticketingIdDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.ticketing_provider_id : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.CustomerNumber : '';
      return this.areDifferent(crm, tkt);
    },
    ssoDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.customer_id : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.SSOId : '';
      return this.areDifferent(crm, tkt);
    },
  },
  methods: {
    resetPagination() {
      this.ssoSeasonPage = 1;
      this.ssoMatchPage = 1;
      this.cnSeasonPage = 1;
      this.cnMatchPage = 1;
    },

    async getCustomersAutocomplete(query, inputTypeName) {
          if (inputTypeName === 'email') {
            this.emailSuggestions = [];
          } else if (inputTypeName === 'provider_ticketing_id') {
            this.ticketingIdSuggestions = [];
          }

          if (query.trim().length > 2) {
            let column = '';
            let limit = 1000;
            let table = '';
            let select = [];

            if (inputTypeName === 'email') {
              column = 'email';
              table = 'crm_user';
              select = ['email'];
            } else if (inputTypeName === 'provider_ticketing_id') {
              column = 'CustomerNumber';
              table = 'crm_tymes4';
              select = ['CustomerNumber'];
            }

            const payload = {
              table,
              column,
              filter: query,
              limit,
              select
            };

            try {
              const response = await General.autocomplete(payload);
              const rawData = Array.isArray(response.data.data) ? response.data.data : [];

              const suggestions = rawData.map(item => {
                if (column === 'email') return { text: item.email };
                if (column === 'CustomerNumber') return { text: item.CustomerNumber };
                return null;
              }).filter(Boolean);

              const uniqueSuggestions = suggestions.filter(
                (v, i, a) => a.findIndex(t => t.text === v.text) === i
              );

              if (inputTypeName === 'email') {
                this.emailSuggestions = uniqueSuggestions;
              } else if (inputTypeName === 'provider_ticketing_id') {
                this.ticketingIdSuggestions = uniqueSuggestions;
              }
            } catch (err) {
              console.error(err);
            }
          }
        },

    async search() {
      this.isBusy = true;
      this.ticketsBusy = true;
      this.hasSearched = true;
      this.resetPagination();
      this.tableData = [];
      this.ticketingData = [];
      this.resetTicketData();
      this.error = null;

      const email = this.filterInput.email.trim();
      const ticketingId = this.filterInput.provider_ticketing_id.trim();

      try {
        // Build filter string for customers endpoint
        let filterString = '';
        if (email) {
          filterString += `email=${encodeURIComponent(email)}`;
        }
        if (ticketingId) {
          filterString += filterString ? '&' : '';
          filterString += `provider_ticketing_id=${encodeURIComponent(ticketingId)}`;
        }

        // Call both endpoints in parallel
        const [customerResponse, ticketingResponse] = await Promise.all([
          filterString ? Customers.getCustomersByFilter(filterString) : Promise.resolve({ data: { data: [] } }),
          (email || ticketingId) ? Ticketing.getUserByCrmId(email || ticketingId) : Promise.resolve({ data: { data: [] } })
        ]);

        this.tableData = customerResponse.data.data || [];

        // Store full ticketing response
        this.ticketingData = ticketingResponse.data || null;

        await this.getCustomerTickets();

      } catch (error) {
        this.error = error.response?.data?.error || 'An error occurred';
        console.error(error);
      } finally {
        this.isBusy = false;
      }
    },

    resetFilters() {
      this.filterInput.email = '';
      this.filterInput.provider_ticketing_id = '';
      this.resetPagination();
      this.tableData = [];
      this.ticketingData = null;
      this.resetTicketData();
      this.ticketsBusy = false;
      this.hasSearched = false;
      this.emailSuggestions = [];
      this.ticketingIdSuggestions = [];
      this.error = null;
    },

    resetTicketData() {
      this.seasonTicketsShowAll = [];
      this.matchTicketsDefault = [];
      this.matchTicketsByCustomerNumber = [];
      this.seasonTicketsByCustomerNumber = [];
    },

    getSearchCustomerId() {
      if (this.customerRow && this.customerRow.customer_id) {
        return this.customerRow.customer_id;
      }
      if (this.ticketingOriginal && this.ticketingOriginal.SSOId) {
        return this.ticketingOriginal.SSOId;
      }
      return null;
    },

    async getCustomerTickets() {
      const customerId = this.getSearchCustomerId();
      this.resetPagination();
      this.resetTicketData();

      if (!customerId) {
        return;
      }

      this.ticketsBusy = true;

      try {
        const [
          seasonShowAllResponse,
          matchDefaultResponse,
          matchByCustomerNumberResponse,
          seasonByCustomerNumberResponse,
        ] = await Promise.all([
          Ticketing.getCustomerSeasonTickets(`show_all=Y&customerID=${encodeURIComponent(customerId)}`),
          Ticketing.getCustomerMatchTickets(`customerID=${encodeURIComponent(customerId)}`),
          Ticketing.getCustomerMatchTickets(`customerID=${encodeURIComponent(customerId)}&ticketsBy=customernumber`),
          Ticketing.getCustomerSeasonTickets(`customerID=${encodeURIComponent(customerId)}&ticketsBy=customernumber`),
        ]);

        this.seasonTicketsShowAll = seasonShowAllResponse.data.data || [];
        this.matchTicketsDefault = matchDefaultResponse.data.data || [];
        this.matchTicketsByCustomerNumber = matchByCustomerNumberResponse.data.data || [];
        this.seasonTicketsByCustomerNumber = seasonByCustomerNumberResponse.data.data || [];
      } catch (error) {
        this.error = error.response?.data?.error || 'An error occurred while loading tickets';
        console.error(error);
      } finally {
        this.ticketsBusy = false;
      }
    },

    formatPrice(value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? value : numberValue.toFixed(2);
    },

    areDifferent(a, b) {
      const norm = (val) =>
        (val === null || val === undefined)
          ? ''
          : String(val).trim().toLowerCase();
      return norm(a) !== norm(b);
    },

    callModalViewMatchTicket(data) {
      this.modalData = data;
      this.$bvModal.show("match_ticket");
    },

    callModalViewSeasonTicket(data) {
      this.modalData = data;
      this.$bvModal.show("season_ticket");
    },
  },
  middleware: "authentication",
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Filters Card -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Search Filters</h4>
            <div class="row">
              <div class="col-sm-12 col-md-4">
                <b-form-group label="Email" label-for="formrow-email-input" class="mb-3">
                  <vue-bootstrap-typeahead
                    id="formrow-email-input"
                    ref="emailAutocomplete"
                    v-model.trim="filterInput.email"
                    :data="emailSuggestions"
                    :max-matches="50"
                    :serializer="s => s.text"
                    placeholder="Enter email..."
                  />
                </b-form-group>
              </div>
              <div class="col-sm-12 col-md-4">
                <b-form-group label="Ticketing Customer Number" label-for="formrow-ticketing-id-input" class="mb-3">
                  <vue-bootstrap-typeahead
                    id="formrow-ticketing-id-input"
                    ref="ticketingIdAutocomplete"
                    v-model.trim="filterInput.provider_ticketing_id"
                    :data="ticketingIdSuggestions"
                    :max-matches="50"
                    :serializer="s => s.text"
                    placeholder="Enter customer number..."
                  />
                </b-form-group>
              </div>
              <div class="col-sm-12 col-md-4 d-flex align-items-end mb-3">
                <button type="button" class="btn btn-success me-2" @click="search">
                  <i class="mdi mdi-magnify me-1"></i> Search
                </button>
                <button type="button" class="btn btn-primary" @click="resetFilters">
                  <i class="mdi mdi-refresh me-1"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Prevent typeahead dropdown clipping before first search -->
    <div v-if="!isBusy && !hasSearched" style="min-height: 280px;"></div>

    <!-- Single centered loader -->
    <div v-if="isBusy" class="d-flex align-items-center justify-content-center" style="min-height: 400px;">
      <b-spinner variant="primary" style="width: 2rem; height: 2rem;"></b-spinner>
      <p class="ms-2 mb-0 small"> Loading Ticketing Data...</p>
    </div>

    <!-- Results (only after a search completes) -->
    <template v-else-if="hasSearched">
    <!-- Split Screen Results -->
    <div class="row">
      <!-- Left Side - Customer Data -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Customer Data</h4>

            <div v-if="!customerRow" class="text-muted">
              No customer data found
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered mb-0">
                <tbody>
                  <tr>
                    <th class="w-50">SSO Id</th>
                    <td :class="{ 'text-danger': ssoDifferent }">{{ customerRow.customer_id }}</td>
                  </tr>
                  <tr>
                    <th>Full Name</th>
                    <td :class="{ 'text-danger': fullNameDifferent }">
                      {{ customerRow.first_name }}
                      <span v-if="customerRow.middle_name"> {{ customerRow.middle_name }}</span>
                      {{ customerRow.last_name }}
                    </td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td :class="{ 'text-danger': emailDifferent }">
                      {{ customerRow.email }}
                    </td>
                  </tr>
                  <tr>
                    <th>Date of Birth</th>
                    <td :class="{ 'text-danger': dobDifferent }">
                      {{ customerRow.date_of_birth }}
                    </td>
                  </tr>
                  <tr>
                    <th>Gender</th>
                    <td :class="{ 'text-danger': genderDifferent }">
                      {{ customerRow.gender }}
                    </td>
                  </tr>
                  <tr>
                    <th>Address</th>
                    <td :class="{ 'text-danger': addressDifferent }">
                      {{ customerRow.address_line1 }}
                      <span v-if="customerRow.address_line2"> {{ customerRow.address_line2 }}</span>
                      <span v-if="customerRow.address_line3"> {{ customerRow.address_line3 }}</span>,
                      {{ customerRow.address_city }}
                      {{ customerRow.address_postal }},
                      {{ customerRow.address_country }}
                    </td>
                  </tr>
                  <tr>
                    <th>Phone Number</th>
                    <td :class="{ 'text-danger': phoneDifferent }">
                      {{ customerRow.phone_number }}
                    </td>
                  </tr>
                  <tr>
                    <th>Ticketing Customer Number</th>
                    <td :class="{ 'text-danger': ticketingIdDifferent }">
                      {{ customerRow.ticketing_provider_id }}
                    </td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>{{ customerRow.status }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side - Ticketing Data -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Ticketing Data</h4>

            <div v-if="!ticketingRow || !ticketingOriginal" class="text-muted">
              No ticketing data found
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered mb-0">
                <tbody>
                  <tr>
                    <th>SSO Id</th>
                    <td :class="{ 'text-danger': ssoDifferent }">
                      {{ ticketingOriginal.SSOId }}
                    </td>
                  </tr>
                  <tr>
                    <th>Full Name</th>
                    <td :class="{ 'text-danger': fullNameDifferent }">
                      {{ ticketingOriginal.FirstName }}
                      <span v-if="ticketingOriginal.MiddleName"> {{ ticketingOriginal.MiddleName }}</span>
                      {{ ticketingOriginal.LastName }}
                    </td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td :class="{ 'text-danger': emailDifferent }">
                      {{ ticketingOriginal.Email }}
                    </td>
                  </tr>
                  <tr>
                    <th>Date Of Birth</th>
                    <td :class="{ 'text-danger': dobDifferent }">
                         {{ ticketingOriginal.DateOfBirth && ticketingOriginal.DateOfBirth.split('T')[0] }}
                    </td>
                  </tr>
                  <tr>
                    <th>Gender</th>
                    <td :class="{ 'text-danger': genderDifferent }">
                      {{ ticketingOriginal.gender }}
                    </td>
                  </tr>
                  <tr>
                    <th>Address</th>
                    <td :class="{ 'text-danger': addressDifferent }">
                      {{ ticketingOriginal.Street }}
                      <span v-if="ticketingOriginal.StreetNr"> {{ ticketingOriginal.StreetNr }}</span>,
                      {{ ticketingOriginal.City }}
                      {{ ticketingOriginal.PostalCode }},
                      {{ ticketingRow.countryIso2 }}
                    </td>
                  </tr>
                  <tr>
                    <th>Phone Number</th>
                    <td :class="{ 'text-danger': phoneDifferent }">
                      {{ ticketingOriginal.TelephoneNr }}
                    </td>
                  </tr>
                  <tr>
                    <th>Ticketing Customer Number</th>
                    <td :class="{ 'text-danger': ticketingIdDifferent }">
                      {{ ticketingOriginal.CustomerNumber }}
                    </td>
                  </tr>
                  <tr>
                    <th class="w-50">Provider</th>
                    <td>{{ ticketingRow.provider }}</td>
                  </tr>
                  <tr>
                    <th>Customer Type</th>
                    <td :class="{ 'text-danger': emailDifferent && ticketingOriginal.CustomerType === 1 }">{{ ticketingOriginal.CustomerType }}</td>
                  </tr>
                  <tr>
                    <th>Sales Group Id</th>
                    <td :class="{ 'text-danger': emailDifferent && ticketingOriginal.CustomerType === 1 }">{{ ticketingOriginal.SalesGroupId }}</td>
                  </tr>

                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Ticketing Tickets -->
    <div class="row">
      <!-- Left - Ticketing by SSO ID -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Ticketing by SSO ID</h4>

            <h5 class="mb-3">Season Tickets</h5>
            <div class="table-responsive mb-2">
              <b-table
                :items="seasonTicketsShowAll"
                :fields="seasonFields"
                responsive="sm"
                :per-page="perPage"
                :current-page="ssoSeasonPage"
                show-empty
                empty-text="No Data Found"
              >
                <template v-slot:cell(subscription_number)="data">
                  <span>
                    {{ data.item.subscription_number }}
                    <span v-if="data.item.siip" class="badge bg-danger ml-2">Siip</span>
                  </span>
                </template>
                <template v-slot:cell(price)="data">
                  {{ formatPrice(data.item.price) }}
                </template>
                <template v-slot:cell(action)="data">
                  <ul class="list-inline mb-0 d-flex align-items-center">
                    <li class="list-inline-item">
                      <a href="javascript:void(0);" @click="callModalViewSeasonTicket(data.item)" class="text-primary" title="View Ticket">
                        <i class="uil uil-eye font-size-18"></i>
                      </a>
                    </li>
                    <li class="list-inline-item" v-if="data.item.business_ticket">
                      <i title="This is a business ticket" class="fas fa-briefcase"></i>
                    </li>
                    <li class="list-inline-item" v-if="data.item.shared">
                      <i title="This ticket is shared to you" class="fas fa-share"></i>
                    </li>
                  </ul>
                </template>
              </b-table>
            </div>
            <div v-if="seasonTicketsShowAll.length > 0" class="dataTables_paginate paging_simple_numbers d-flex justify-content-end mb-4">
              <ul class="pagination pagination-rounded mb-0">
                <b-pagination v-model="ssoSeasonPage" :total-rows="seasonTicketsShowAll.length" :per-page="perPage"></b-pagination>
              </ul>
            </div>

            <h5 class="mb-3">Match Tickets</h5>
            <div class="table-responsive mb-2">
              <b-table
                :items="matchTicketsDefault"
                :fields="matchFields"
                responsive="sm"
                :per-page="perPage"
                :current-page="ssoMatchPage"
                show-empty
                empty-text="No Data Found"
              >
                <template v-slot:cell(match.match_id)="data">
                  <span>
                    <template v-if="data.item.match">{{ data.item.match.home_team_name }} - {{ data.item.match.away_team_name }}</template>
                    <span v-if="data.item.siip" class="badge bg-danger ml-2">Siip</span>
                  </span>
                </template>
                <template v-slot:cell(ticket_price)="data">
                  {{ formatPrice(data.item.ticket_price) }}
                </template>
                <template v-slot:cell(season_ticket)="data">
                  <span v-if="data.item.season_ticket && data.item.season_ticket.customer_season_id && data.item.season_ticket.season_name && data.item.season_ticket.season_product_id" :title="data.item.season_ticket.season_name" class="cursor-pointer">
                    <i class="mdi mdi-check-circle font-size-22 m-0 p-0 text-success"></i>
                  </span>
                  <span v-else class="font-size-13">
                    <i class="fas fa-times-circle font-size-20 m-0 p-0 text-danger"></i>
                  </span>
                </template>
                <template v-slot:cell(action)="data">
                  <ul class="list-inline mb-0 d-flex align-items-center">
                    <li class="list-inline-item">
                      <a href="javascript:void(0);" @click="callModalViewMatchTicket(data.item)" class="text-primary" title="View Ticket">
                        <i class="uil uil-eye font-size-18"></i>
                      </a>
                    </li>
                    <li class="list-inline-item" v-if="data.item.business_ticket">
                      <i title="This is a business ticket" class="fas fa-briefcase"></i>
                    </li>
                    <li class="list-inline-item" v-if="data.item.shared">
                      <i title="This ticket is shared to you" class="fas fa-share"></i>
                    </li>
                  </ul>
                </template>
              </b-table>
            </div>
            <div v-if="matchTicketsDefault.length > 0" class="dataTables_paginate paging_simple_numbers d-flex justify-content-end">
              <ul class="pagination pagination-rounded mb-0">
                <b-pagination v-model="ssoMatchPage" :total-rows="matchTicketsDefault.length" :per-page="perPage"></b-pagination>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Right - Ticketing by Customer Number -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Ticketing by Customer Number</h4>

            <h5 class="mb-3">Season Tickets</h5>
            <div class="table-responsive mb-2">
              <b-table
                :items="seasonTicketsByCustomerNumber"
                :fields="seasonFields"
                responsive="sm"
                :per-page="perPage"
                :current-page="cnSeasonPage"
                show-empty
                empty-text="No Data Found"
              >
                <template v-slot:cell(subscription_number)="data">
                  <span>
                    {{ data.item.subscription_number }}
                    <span v-if="data.item.siip" class="badge bg-danger ml-2">Siip</span>
                  </span>
                </template>
                <template v-slot:cell(price)="data">
                  {{ formatPrice(data.item.price) }}
                </template>
                <template v-slot:cell(action)="data">
                  <ul class="list-inline mb-0 d-flex align-items-center">
                    <li class="list-inline-item">
                      <a href="javascript:void(0);" @click="callModalViewSeasonTicket(data.item)" class="text-primary" title="View Ticket">
                        <i class="uil uil-eye font-size-18"></i>
                      </a>
                    </li>
                    <li class="list-inline-item" v-if="data.item.business_ticket">
                      <i title="This is a business ticket" class="fas fa-briefcase"></i>
                    </li>
                    <li class="list-inline-item" v-if="data.item.shared">
                      <i title="This ticket is shared to you" class="fas fa-share"></i>
                    </li>
                  </ul>
                </template>
              </b-table>
            </div>
            <div v-if="seasonTicketsByCustomerNumber.length > 0" class="dataTables_paginate paging_simple_numbers d-flex justify-content-end mb-4">
              <ul class="pagination pagination-rounded mb-0">
                <b-pagination v-model="cnSeasonPage" :total-rows="seasonTicketsByCustomerNumber.length" :per-page="perPage"></b-pagination>
              </ul>
            </div>

            <h5 class="mb-3">Match Tickets</h5>
            <div class="table-responsive mb-2">
              <b-table
                :items="matchTicketsByCustomerNumber"
                :fields="matchFields"
                responsive="sm"
                :per-page="perPage"
                :current-page="cnMatchPage"
                show-empty
                empty-text="No Data Found"
              >
                <template v-slot:cell(match.match_id)="data">
                  <span>
                    <template v-if="data.item.match">{{ data.item.match.home_team_name }} - {{ data.item.match.away_team_name }}</template>
                    <span v-if="data.item.siip" class="badge bg-danger ml-2">Siip</span>
                  </span>
                </template>
                <template v-slot:cell(ticket_price)="data">
                  {{ formatPrice(data.item.ticket_price) }}
                </template>
                <template v-slot:cell(season_ticket)="data">
                  <span v-if="data.item.season_ticket && data.item.season_ticket.customer_season_id && data.item.season_ticket.season_name && data.item.season_ticket.season_product_id" :title="data.item.season_ticket.season_name" class="cursor-pointer">
                    <i class="mdi mdi-check-circle font-size-22 m-0 p-0 text-success"></i>
                  </span>
                  <span v-else class="font-size-13">
                    <i class="fas fa-times-circle font-size-20 m-0 p-0 text-danger"></i>
                  </span>
                </template>
                <template v-slot:cell(action)="data">
                  <ul class="list-inline mb-0 d-flex align-items-center">
                    <li class="list-inline-item">
                      <a href="javascript:void(0);" @click="callModalViewMatchTicket(data.item)" class="text-primary" title="View Ticket">
                        <i class="uil uil-eye font-size-18"></i>
                      </a>
                    </li>
                    <li class="list-inline-item" v-if="data.item.business_ticket">
                      <i title="This is a business ticket" class="fas fa-briefcase"></i>
                    </li>
                    <li class="list-inline-item" v-if="data.item.shared">
                      <i title="This ticket is shared to you" class="fas fa-share"></i>
                    </li>
                  </ul>
                </template>
              </b-table>
            </div>
            <div v-if="matchTicketsByCustomerNumber.length > 0" class="dataTables_paginate paging_simple_numbers d-flex justify-content-end">
              <ul class="pagination pagination-rounded mb-0">
                <b-pagination v-model="cnMatchPage" :total-rows="matchTicketsByCustomerNumber.length" :per-page="perPage"></b-pagination>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    </template>

    <!-- MODALS -->
    <modalViewMatchTicket :ticketData="modalData" />
    <modalViewSeasonTicket :ticketData="modalData" />
    <!-- END MODALS -->

  </Layout>
</template>

